import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { GeographicInfoState } from "@/Common/Models/Localization/GeographicInfoState";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { HealthCardValidatorFactory } from "./HealthCardValidator";

interface IHealthCardComponentProps {
    healthCard: string;
    expiryDate: Date;
    regionLookupId: string;
    countryLookupId: string;
    isValid: boolean;
    isDisabled?: boolean;

    onHealthCardNumberChanged: (number: string) => void;
    onExpiryDateChanged: (date: Date) => void;
    onRegionLookupIdChanged: (regionLookupId: string) => void;
}

class HealthCardComponentState {
    public regions: GeographicInfoState[];
}

class HealthCardComponent extends BaseReactComponent<IHealthCardComponentProps, HealthCardComponentState> {
    private readonly _componentId: string;
    private readonly isOntarioHealthcard: boolean = this.props.regionLookupId === cacheStore.GetLookupIdForProvince(CanadaState.Ontario);

    constructor(props: IHealthCardComponentProps) {
        super(props);
        this._componentId = `HealthCardComponent_${sessionStore.newId()}`;

        const initialState = new HealthCardComponentState();
        initialState.regions = this.isCanada()
            ? cacheStore.GetStateMedfarDictionariesByLookUpTypeCode("CanadaState")
            : [];
        this.state = initialState;
    }

    public render(): JSX.Element {
        const validator = HealthCardValidatorFactory.getValidator(this.props.countryLookupId, this.props.regionLookupId);

        return (
            <div id={this._componentId}>
                <div className={`flex-container ${this.state.regions.any() && "healthcard-control"}`} style={{ justifyContent: "flex-start" }}>
                    <div className="flex-container" style={{ width: "100%" }}>
                        {this.renderRegionSelection()}
                        {this.renderHealthCardTextbox(validator.mask)}
                    </div>
                    {this.renderExpiry()}
                </div>
                {!this.props.isValid &&
                    <p className="label-error">{validator.errorMessage}</p>
                }
            </div>
        );
    }

    private renderRegionSelection(): JSX.Element {
        if (!this.state.regions.any())
            return null;

        let regions = this.state.regions
            .map(region => {
                const displayName = region.Myle_LookupCode.substr(region.Myle_LookupCode.indexOf("_") + 1);
                const lookupId = cacheStore.FindLookupByLookupCode(region.Myle_LookupCode).LookupID;
                return { lookupId, displayName };
            })
            .sortByProperty(x => x.displayName);

        return (
            <div style={{ width: "80px" }}>
                <select
                    id={this._componentId + "_select"}
                    data-cy="healthCardComponent-region-select"
                    className={`form-control ${!this.props.isValid && "invalid"}`}
                    style={{ padding: "5px" }}
                    value={this.props.regionLookupId}
                    onChange={(e: any) => this.props.onRegionLookupIdChanged?.(e.target.value)}
                    disabled={this.props.isDisabled}
                >
                    {regions.map(region => <option key={region.lookupId} value={region.lookupId}>{region.displayName}</option>)}
                </select>
            </div>
        );
    }

    private renderHealthCardTextbox(mask: string): JSX.Element {
        return (
            <div style={{ width: "100%" }}>
                <TextboxMaskedControl
                    focus
                    id={this._componentId + "_textbox"}
                    dataCy="healthCardComponent-NAM-textbox"
                    value={this.props.healthCard}
                    mask={this.props.healthCard?.length === 10 && this.isOntarioHealthcard ? null : mask}
                    isValid={this.props.isValid}
                    onChangeFunction={value => this.props.onHealthCardNumberChanged?.(value)}
                    returnMaskedValue={this.isOntarioHealthcard && this.props.healthCard.length > 10}
                    disabled={this.props.isDisabled}
                />
            </div>
        );
    }

    private renderExpiry(): JSX.Element {
        if (this.isMexico())
            return null;

        const isNewFoundlandRegion = GeographicInfoState.isNewFoundlandProvince(this.props.regionLookupId);

        return (
            <div className="expiry-date">
                <DateSelector
                    id={`${this._componentId}_date-picker`}
                    width={130}
                    cssClass="rounded"
                    key={`${this.props.regionLookupId}_date-picker`}
                    date={this.props.expiryDate}
                    onChangeCallback={date => this.props.onExpiryDateChanged?.(date)}
                    dateFormat={isNewFoundlandRegion ? "yyyy/MM/dd" : "yyyy MMMM"}
                    disabled={this.props.isDisabled}
                    isMonthPicker={!isNewFoundlandRegion}
                />
            </div>
        );
    }

    private isCanada(): boolean {
        const canadaLookup = cacheStore.FindLookupByLookupCode("CNCANADA");
        return canadaLookup.LookupID === this.props.countryLookupId;
    }

    private isMexico(): boolean {
        const mexicoLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");
        return mexicoLookup.LookupID === this.props.countryLookupId;
    }
}

export { HealthCardComponent };
